// SectionHeader — title + "updated Xs ago" + single refresh (Blueprint §4.6 / D-31).
// Replaces the scattered per-panel Refresh buttons with one consistent control.

import type { ReactNode } from "react";
import { RefreshCw } from "lucide-react";

function relativeTime(updatedAt: Date | null): string {
  if (!updatedAt) return "";
  const secs = Math.max(0, Math.round((Date.now() - updatedAt.getTime()) / 1000));
  if (secs < 60) return `updated ${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `updated ${mins}m ago`;
  return `updated ${Math.round(mins / 60)}h ago`;
}

export function SectionHeader({
  title,
  subtitle,
  updatedAt = null,
  onRefresh,
  refreshing = false,
  actions,
}: {
  title: string;
  subtitle?: string;
  updatedAt?: Date | null;
  onRefresh?: () => void;
  refreshing?: boolean;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-black text-slate-900">{title}</h2>
        {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
      </div>
      <div className="flex items-center gap-2">
        {updatedAt ? <span className="text-[11px] text-slate-400">{relativeTime(updatedAt)}</span> : null}
        {actions}
        {onRefresh ? (
          <button
            type="button"
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        ) : null}
      </div>
    </div>
  );
}
